import * as React from "react";
import useSWR from "swr";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";

import { snackbarStore } from "src/store/snackbarStore";
import { useBrandStore } from "src/store/brand/brand.store";

import BrandUpsertDialog, { BrandUpsertPayload } from "src/components/brand/BrandUpsertDialog";
import BrandTable from "src/components/brand/BrandTable";

import {
  getBrandListApi,
  getBrandApi,
  createBrandApi,
  updateBrandApi,
  deleteBrandApi,
  uploadBrandLogoMultipartApi,
  uploadBrandImageMultipartApi,
  // uploadBrandLogoJsonApi,
  // uploadBrandImageJsonApi,
  type BrandItem,
  type BrandListItem,
} from "src/api/brand/brandApi";

const SIZE_OPTIONS = [10, 20, 25, 50, 100];

export default function BrandManagePage() {
  const {
    brandName,
    categoryId,
    isActive,
    isFeatured,
    page,
    size,
    order,
    setQuery,
    dialogOpen,
    editItem,
    openCreate,
    openEdit,
    closeDialog,
  } = useBrandStore();

  const swrKey = React.useMemo(
    () => ["brand/list", brandName, categoryId, isActive, isFeatured, page, size, order] as const,
    [brandName, categoryId, isActive, isFeatured, page, size, order]
  );

  const { data, isLoading, mutate } = useSWR(
    swrKey,
    async () => {
      const apiPage = Math.max(0, page - 1);
      return await getBrandListApi({
        brandName: brandName.trim() || undefined,
        categoryId: categoryId.trim() ? Number(categoryId) : undefined,
        isActive: isActive === "" ? undefined : isActive === "true",
        isFeatured: isFeatured === "" ? undefined : isFeatured === "true",
        page: apiPage,
        size,
        order,
      });
    },
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const list = data?.data?.content ?? [];
  const totalPages = Math.max(1, data?.data?.totalPages ?? 1);
  const totalElements = data?.data?.totalElements ?? 0;

  // edit detail
  const [detail, setDetail] = React.useState<BrandItem | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      if (!dialogOpen || !editItem?.id) {
        setDetail(null);
        return;
      }
      try {
        setDetailLoading(true);
        const res = await getBrandApi(editItem.id);
        if (!alive) return;
        if (res.statusCode === 0) setDetail(res.data);
        else setDetail(null);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [dialogOpen, editItem?.id]);
  const onSubmit = async (payload: BrandUpsertPayload): Promise<void> => {
    try {
      setSaving(true);

      let brandId = editItem?.id;

      const { logoFile, thumbnailFile, ...body } = payload;

      if (brandId) {
        const res = await updateBrandApi(brandId, body, logoFile, thumbnailFile);
        if (res.statusCode !== 1) {
          snackbarStore.getState().addSnackbar({
            message: res.message || "수정 실패",
            severity: "error",
          });
          return;
        }
        snackbarStore.getState().addSnackbar({ message: "수정 완료", severity: "success" });
      } else {
        const res = await createBrandApi(body, logoFile, thumbnailFile);
        if (res.statusCode !== 1) {
          snackbarStore.getState().addSnackbar({
            message: res.message || "등록 실패",
            severity: "error",
          });
          return;
        }
        brandId = res.data?.id;
        snackbarStore.getState().addSnackbar({ message: "등록 완료", severity: "success" });
      }

      if (!brandId) return;

      closeDialog();
      setDetail(null);
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: BrandListItem) => {
    const ok = window.confirm(`"${item.brandNameKo ?? item.id}" 삭제할까?`);
    if (!ok) return;

    try {
      setSaving(true);
      const res = await deleteBrandApi(item.id);
      if (res.statusCode !== 0) {
        snackbarStore.getState().addSnackbar({
          message: res.message || "삭제 실패",
          severity: "error",
        });
        return;
      }

      snackbarStore.getState().addSnackbar({ message: "삭제 완료", severity: "success" });
      await mutate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                브랜드 목록
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                총 {totalElements.toLocaleString()}건
              </Typography>
            </Stack>

            <Button variant="contained" onClick={openCreate} disabled={saving}>
              등록
            </Button>
          </Stack>

          {/* 검색/옵션 */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
            <TextField
              label="brandName"
              value={brandName}
              onChange={(e) => setQuery({ brandName: e.target.value, page: 1 })}
              size="small"
              sx={{ width: 240 }}
            />
            <Button variant="outlined" onClick={() => mutate()} disabled={isLoading}>
              검색
            </Button>

            <Box sx={{ flex: 1 }} />

            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel id="brand-size-label">size</InputLabel>
              <Select
                labelId="brand-size-label"
                label="size"
                value={size}
                onChange={(e) => setQuery({ size: Number(e.target.value) || 20, page: 1 })}
              >
                {SIZE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}개
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <BrandTable
                list={list}
                loading={false}
                disabled={saving}
                onEdit={openEdit}
                onDelete={onDelete}
              />

              <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, nextPage) => setQuery({ page: nextPage })}
                  color="primary"
                  shape="rounded"
                  disabled={isLoading || saving}
                />
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      <BrandUpsertDialog
        open={dialogOpen}
        item={detail}
        listItem={editItem}
        loading={saving || detailLoading}
        onClose={() => {
          closeDialog();
          setDetail(null);
        }}
        onSubmit={onSubmit}
      />
    </>
  );
}
